import { tryParseDate } from "../global.functions";

/** Access level enumeration. */
export enum AccessLevel {
    NoAccess = "N",
    Read = "R",
    Write = "W",
}

/** Account Status enumeration */
export enum AccountStatus {
    Active = "Active",
    Locked = "Locked",
    Unverified = "Unverified",
}

/** Guest Account Status enumeration */
export enum GuestAccountStatus {
    ApplicationLinkEmailed = "Application Link Emailed",
    PasscodeRequested = "Passcode Requested",
    PasscodeExpired = "Passcode Expired",
    Active = "Active",
}

/** All possible User account types. */
export enum AccountType {
    System = "System",
    Service = "Service",
    User = "User",
    Api = "Api",
}

/** ApplicationUserType */
export enum ApplicationUserType {
    Anonymous = "Anonymous",
    Authenticated = "Authenticated",
    Guest = "Guest"
}

/** Denotes a mapping type. */
export enum MapType {
    Field = "Field",
    Value = "Value",
    Content = "Content",
    Property = "Property",
    PrivateValue = "PrivateValue",
    PrivateField = "PrivateField",
}

/** A mapping from an iApply field to an external field. */
export class MappedField implements IMappedField {
    /** Gets or sets the source field or value. */
    source!: string | undefined;
    mapType!: MapType;
    /** Gets or sets the target field name. */
    target!: string | undefined;

    constructor(data?: IMappedField) {
        if (data) {
            for (let property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.source = _data["Source"];
            this.mapType = _data["MapType"];
            this.target = _data["Target"];
        }
    }

    static fromJS(data: any): MappedField {
        data = typeof data === 'object' ? data : {};
        let result = new MappedField();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["source"] = this.source;
        data["mapType"] = this.mapType;
        data["target"] = this.target;
        return data;
    }
}

/** A mapping from an iApply field to an external field. */
export interface IMappedField {
    /** Gets or sets the source field or value. */
    source: string | undefined;
    mapType: MapType;
    /** Gets or sets the target field name. */
    target: string | undefined;
}

/** An enumeration of HTTP Request Methods, as defined by the HTTP/1.1 specification. */
export enum HttpRequestMethodType {
    GET = "GET",
    HEAD = "HEAD",
    POST = "POST",
    PUT = "PUT",
    DELETE = "DELETE",
    TRACE = "TRACE",
    OPTIONS = "OPTIONS",
    CONNECT = "CONNECT",
}

/** Service data format enumeration. */
export enum ServiceDataFormat {
    HttpVariables = "HttpVariables",
    Json = "Json",
    Xml = "Xml",
}

export enum ProductWorkflowState {
    // Unknown state.
    Unknown = "Unknown",
    // Draft state.
    Draft = "Draft",
    // Pending approval state.
    PendingApproval = "PendingApproval",
    // Current state.
    Current = "Current",
    // Rejected state.
    Rejected = "Rejected",
    // Obsolete state.
    Obsolete = "Obselete"
}

/** Pagination */
export class Pagination {
    pageIndex: number = 0;
    pageSize: number = 10;
    totalRecords: number = 0;
    numPages: number = 0;
    skipRecords: number = undefined;
    takeRecords: number = undefined;

    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }

        this.pageIndex = _data["pageIndex"];
        this.pageSize = _data["pageSize"];
        this.totalRecords = _data["totalRecords"];
        this.numPages = _data["numPages"];
        this.skipRecords = _data["skipRecords"];
        this.takeRecords = _data["takeRecords"];

        return this;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["pageIndex"] = this.pageIndex;
        data["pageSize"] = this.pageSize;
        data["totalRecords"] = this.totalRecords;
        data["numPages"] = this.numPages;
        data["skipRecords"] = this.skipRecords;
        data["takeRecords"] = this.takeRecords;
        return data;
    }

    equals(pagination?: Pagination): boolean {
        return !!pagination
            && this.pageIndex == pagination.pageIndex
            && this.pageSize == pagination.pageSize
            // && this.numPages == pagination.numPages
            ;
    }
}

/** KeyValuePair item for representing an entity. */
export class KeyValuePair implements IKeyValuePair {
    /** Gets or sets the entity key. */
    key!: string | undefined;
    /** Gets or sets the entity value/name/description. */
    value!: string | undefined;

    constructor(data?: IKeyValuePair) {
        if (data) {
            for (var property in data) {
                if (data.hasOwnProperty(property))
                    (<any>this)[property] = (<any>data)[property];
            }
        }
    }

    init(_data?: any) {
        if (_data) {
            this.key = _data["Key"];
            this.value = _data["Value"];
        }
    }

    static fromJS(data: any): KeyValuePair {
        data = typeof data === 'object' ? data : {};
        let result = new KeyValuePair();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["Key"] = this.key;
        data["Value"] = this.value;
        return data;
    }
}

export interface IKeyValuePair {
    key: string;
    value: string;
};

export interface IDateTimeRange {
    from: string;  //JavaScript Date or Luxon DateTime;
    to: string;  //Time;
}

export class DateTimeRange implements IDateTimeRange {
    from: string;  //;
    to: string;  //Date or DateTime;

    constructor(data?: IDateTimeRange) {
        if (data) {
            this.init(data);
            // for (let property in data) {
            //     if (data.hasOwnProperty(property))
            //         (<any>this)[property] = (<any>data)[property];
            // }
        }
    }

    init(_data?: any): this {
        if (_data) {
            this.from = _data["From"] ?? _data["from"];
            this.to = _data["To"] ?? _data["to"];
        }

        return this;
    }

    static fromJS(data: any): DateTimeRange {
        data = typeof data === 'object' ? data : {};
        let result = new DateTimeRange();
        result.init(data);
        return result;
    }

    toJSON(data?: any) {
        data = typeof data === 'object' ? data : {};
        data["from"] = this.from;
        data["to"] = this.to;
        return data;
    }
}

export abstract class CommonBaseModel {
    init(_data?: any): this {
        if (!_data || typeof _data !== 'object') {
            return this;
        }

        _data = _data["response"] ? _data["response"] : _data;

        for (let property in _data) {
            if (Object.hasOwn(_data, property)) {
                let value = _data[property];
                let dateValue = tryParseDate(value);
                (<any>this)[lowerFirstLetter(property)] = dateValue ?? value;
            }
        }

        if (_data["_id"] && _data["_id"]["$oid"]) {
            this["id"] = _data["_id"]["$oid"];
        }

        return this;
    };

    /**
     * Parse an array of values and populate the local array.
     * @param objToInsert The object
     */
    initArray(objToInsert: object, propInsert: string, dataFrom: object) {
        objToInsert[propInsert] = [];
        if (!dataFrom || !Array.isArray(dataFrom)) {
            return;
        }

        for (let item of dataFrom) {
            if (!item) {
                continue;
            }

            objToInsert[propInsert].push(item);
        }
    }
}

function lowerFirstLetter(string) {
    if (!string) {
        return string;
    }

    return string.charAt(0).toLowerCase() + string.slice(1);
}

export class FormServiceModelFactory<T extends CommonBaseModel> {
    fromJS(type: { new(): T; }, data: any): T {
        data = typeof data === 'object' ? data : {};
        return (new type()).init(data);
    }
}

export class Size {
    width: number;
    height: number;

    constructor(width: number, height: number) {
        this.width = width;
        this.height = height;
    }

    reverse() {
        let swap = this.width;
        this.width = this.height;
        this.height = swap;
    }
}